import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaDownload,
  FaSpinner
} from 'react-icons/fa';
import { ModelListItemProps } from '../types';
import { formatFileSizeFromMB } from '../../../../data-cloud/utils/chartHelpers';

/** Get badge color classes based on model type */
const getModelTypeBadgeColor = (type: string) => {
  const colorMap: Record<string, string> = {
    'lstm': 'bg-accent text-primary',
    'svr': 'bg-emerald-50 text-emerald-700',
    'hybrid': 'bg-violet-50 text-violet-700',
    'best_model': 'bg-amber-50 text-amber-700'
  };
  return colorMap[type.toLowerCase()] || 'bg-muted text-muted-foreground';
};

/** Get accent border color based on model type */
const getModelTypeAccent = (type: string) => {
  const colorMap: Record<string, string> = {
    'lstm': 'bg-primary',
    'svr': 'bg-emerald-500',
    'hybrid': 'bg-violet-500',
    'best_model': 'bg-amber-500'
  };
  return colorMap[type.toLowerCase()] || 'bg-muted-foreground';
};

/** Get display name for model type */
const getModelTypeDisplay = (type: string) => {
  const typeMap: Record<string, string> = {
    'lstm': 'LSTM',
    'svr': 'SVR',
    'hybrid': 'Hybrid',
    'best_model': 'Best Model'
  };
  return typeMap[type.toLowerCase()] || type;
};

/**
 * Single model item in the sidebar list
 * Shows model info with download and comparison options
 */
export const ModelListItem: React.FC<ModelListItemProps> = memo(({
  model,
  isSelected,
  isDownloading,
  onSelect,
  onDownload,
  formatDate
}) => {
  const { t } = useTranslation('training');

  const handleClick = () => onSelect(model);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownload(model.filename);
  };

  return (
    <div
      onClick={handleClick}
      className={`
        group relative rounded-lg cursor-pointer transition-all duration-150
        ${isSelected
          ? 'bg-accent/70 border border-primary/20 shadow-sm ring-1 ring-primary/10'
          : 'bg-card border border-border hover:border-border/60 hover:shadow-sm'
        }
      `}
    >
      {/* Left accent */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 rounded-l-lg ${getModelTypeAccent(model.model_type)}`} />

      <div className="p-3 pl-3.5">
        {/* Header: name + badge */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-sm font-medium text-foreground truncate">
            {model.filename.replace('.h5', '')}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tracking-wide uppercase flex-shrink-0 ${getModelTypeBadgeColor(model.model_type)}`}>
            {getModelTypeDisplay(model.model_type)}
          </span>
        </div>

        {/* Metadata */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <span>{formatFileSizeFromMB(model.file_size_mb)}</span>
          <span className="w-px h-3 bg-border" />
          <span>{formatDate(model.modified_time)}</span>
        </div>

        {/* Metrics preview */}
        {model.metrics && (model.metrics.r2 !== undefined || model.metrics.rmse !== undefined) && (
          <div className="flex items-center gap-3 text-xs mb-2">
            {model.metrics.r2 !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">R²</span>
                <span className="font-medium text-foreground">{model.metrics.r2.toFixed(4)}</span>
              </div>
            )}
            {model.metrics.rmse !== undefined && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">RMSE</span>
                <span className="font-medium text-foreground">{model.metrics.rmse.toFixed(4)}</span>
              </div>
            )}
          </div>
        )}

        {/* Download button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className={`
              flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150
              ${isDownloading
                ? 'bg-accent text-primary cursor-wait'
                : 'bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground active:scale-[0.97]'
              }
            `}
            title={t('training.sidebar.download', 'Download model')}
          >
            {isDownloading ? (
              <FaSpinner className="animate-spin text-[10px]" />
            ) : (
              <FaDownload className="text-[10px]" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

ModelListItem.displayName = 'ModelListItem';

export default ModelListItem;
